import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialState = { name: "", email: "", phone: "", message: "" };

export default function Contact() {
  const headRef = useReveal();
  const formRef = useReveal();
  const { t } = useLanguage();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t.contact.toasts.validation);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      });
      toast.success(t.contact.toasts.success);
      setForm(initialState);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string" && detail.length > 0
          ? detail
          : t.contact.toasts.error;
      toast.error(msg || t.contact.toasts.genericError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contatti"
      data-testid="contact-section"
      className="py-24 md:py-36 bg-[#FAF9F5]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div ref={headRef} className="reveal max-w-3xl mb-20 md:mb-28">
          <span className="overline block mb-5">{t.contact.overline}</span>
          <h2 className="font-serif-display text-4xl md:text-6xl font-light tracking-tight text-[#1A1C19] leading-[1.02]">
            {t.contact.titleLine1}
            <span className="italic block text-[#363A33]">
              {t.contact.titleLine2}
            </span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#5C6058] leading-relaxed">
            {t.contact.description}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 space-y-10">
            <InfoBlock
              icon={MapPin}
              label={t.contact.labels.address}
              lines={t.contact.addressLines}
              testId="contact-address"
            />
            <InfoBlock
              icon={Phone}
              label={t.contact.labels.phone}
              lines={[
                <a
                  key="tel"
                  href="tel:+3901432935"
                  className="hover:text-[#A88B7D] transition-colors"
                  data-testid="contact-phone-link"
                >
                  +39 0143 2935
                </a>,
              ]}
            />
            <InfoBlock
              icon={Mail}
              label={t.contact.labels.email}
              lines={[
                <a
                  key="mail"
                  href="mailto:info@langelosas.com"
                  className="hover:text-[#A88B7D] transition-colors"
                  data-testid="contact-email-link"
                >
                  info@langelosas.com
                </a>,
              ]}
            />
          </div>

          <form
            ref={formRef}
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="reveal lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10"
          >
            <Field
              label={t.contact.form.name}
              name="name"
              value={form.name}
              onChange={handleChange}
              testId="contact-input-name"
              required
            />
            <Field
              label={t.contact.form.email}
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              testId="contact-input-email"
              required
            />
            <Field
              label={t.contact.form.phone}
              name="phone"
              value={form.phone}
              onChange={handleChange}
              testId="contact-input-phone"
            />
            <div className="md:col-span-2">
              <label className="overline block mb-3">
                {t.contact.form.message}
              </label>
              <textarea
                name="message"
                rows={5}
                value={form.message}
                onChange={handleChange}
                data-testid="contact-input-message"
                required
                className="input-editorial resize-none"
                placeholder={t.contact.form.messagePlaceholder}
              />
            </div>

            <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center gap-6 pt-4">
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="btn-primary"
              >
                {loading ? t.contact.form.submitting : t.contact.form.submit}
              </button>
              <p className="text-xs text-[#5C6058] max-w-sm">
                {t.contact.form.privacy}
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function InfoBlock({ icon: Icon, label, lines, testId }) {
  return (
    <div data-testid={testId} className="flex gap-5">
      <div className="pt-1">
        <Icon size={18} strokeWidth={1.25} className="text-[#A88B7D]" />
      </div>
      <div>
        <div className="overline mb-2">{label}</div>
        <div className="text-base md:text-lg text-[#1A1C19] leading-relaxed font-light">
          {lines.map((l, i) => (
            <div key={i}>{l}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required, testId, className = "" }) {
  return (
    <div className={className}>
      <label className="overline block mb-3">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="input-editorial"
      />
    </div>
  );
}
